import React, { useMemo, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ResponsiveSunburst } from '@nivo/sunburst';
import './SwotRingChart.css';

interface SubDimension {
  key: string;
  label: string;
  score: number | null;
  category: string | null;
  source?: string;
  points?: string[];
}

interface Dimension {
  key: string;
  label: string;
  score: number | null;
  category: string | null;
  sub_dimensions: SubDimension[];
}

interface Area {
  key: string;
  label: string;
  score: number | null;
  category: string | null;
  dimensions: Dimension[];
}

interface SwotRingChartProps {
  areas: Area[];
  size?: number;
  onItemClick?: (item: SubDimension) => void;
}

// Premium SWOT color palette - green/teal theme
const SWOT_THEME = {
  S: {
    primary: '#00FF88',    // Bright green - Strength
    secondary: '#33FF9F',
    tertiary: '#66FFB6',
    dark: '#00CC6D',
    glow: 'rgba(0, 255, 136, 0.3)',
    label: 'Strength',
  },
  O: {
    primary: '#7FFF00',    // Lime green - Improvements (Opportunity)
    secondary: '#99FF33',
    tertiary: '#B3FF66',
    dark: '#66CC00',
    glow: 'rgba(127, 255, 0, 0.3)',
    label: 'Improvements',
  },
  W: {
    primary: '#BA68C8',    // Purple - Weakness
    secondary: '#CE93D8',
    tertiary: '#E1BEE7',
    dark: '#9C27B0',
    glow: 'rgba(186, 104, 200, 0.3)',
    label: 'Weakness',
  },
  T: {
    primary: '#008B8B',    // Dark teal - Critical (Threat)
    secondary: '#00A2A2',
    tertiary: '#00B9B9',
    dark: '#006F6F',
    glow: 'rgba(0, 139, 139, 0.3)',
    label: 'Critical',
  },
};

// Get gradient color for dimensions based on depth
const getLayerColor = (category: string, depth: number, index: number): string => {
  const theme = SWOT_THEME[category as keyof typeof SWOT_THEME];
  if (!theme) return '#4b5563';
  
  // Inner ring (SWOT category) - use primary
  if (depth === 1) return theme.primary;
  
  // Middle ring (Dimension) - alternate between dark and primary
  if (depth === 2) {
    const colors = [theme.dark, theme.primary, theme.secondary];
    return colors[index % colors.length];
  }
  
  // Outer ring (Sub-dimension) - use secondary/tertiary
  const outerColors = [theme.secondary, theme.tertiary, theme.primary];
  return outerColors[index % outerColors.length];
};

// Transform data to Nivo format
interface NivoNode {
  id: string;
  name: string;
  color?: string;
  value?: number;
  children?: NivoNode[];
  category?: string;
  score?: number | null;
  source?: string;
  depth?: number;
  dimensionName?: string;
  itemData?: SubDimension;
}

// Tooltip content component
const TooltipContent = ({ data }: { data: any }) => {
  const theme = data.category ? SWOT_THEME[data.category as keyof typeof SWOT_THEME] : null;
  
  const isCategory = data.depth === 1;
  const isDimension = data.depth === 2;
  const isSubDimension = data.depth === 3;
  
  return (
    <>
      {/* Category Badge */}
      {data.category && theme && (
        <div className="swot-tooltip-category-badge" style={{ 
          backgroundColor: theme.glow,
          color: theme.primary,
        }}>
          {theme.label}
        </div>
      )}
      
      {/* Hierarchy Display */}
      <div className="swot-tooltip-hierarchy">
        {/* Dimension Level */}
        {(isDimension || isSubDimension) && (
          <div className="swot-tooltip-level">
            <div className="swot-tooltip-level-header">
              <span className="swot-tooltip-level-icon">▸</span>
              <span className="swot-tooltip-level-label">Dimension</span>
            </div>
            <div className="swot-tooltip-level-content">
              <span className="swot-tooltip-level-name">
                {isDimension ? data.name : data.dimensionName}
              </span>
            </div>
          </div>
        )}
        
        {/* Sub-dimension Level */}
        {isSubDimension && (
          <div className="swot-tooltip-level subdimension">
            <div className="swot-tooltip-level-header">
              <span className="swot-tooltip-level-icon">▸</span>
              <span className="swot-tooltip-level-label">Sub-dimension</span>
            </div>
            <div className="swot-tooltip-level-content">
              <span className="swot-tooltip-level-name">{data.name}</span>
              {data.score !== undefined && data.score !== null && (
                <span className="swot-tooltip-level-score" style={{ color: theme?.primary }}>
                  {data.score}
                </span>
              )}
            </div>
          </div>
        )}
        
        {/* Category Level Only */}
        {isCategory && (
          <div className="swot-tooltip-level">
            <div className="swot-tooltip-level-content">
              <span className="swot-tooltip-level-name">{data.name}</span>
            </div>
          </div>
        )}
      </div>
      
      {/* Source */}
      {data.source && (
        <div className="swot-tooltip-meta">
          <span className="swot-tooltip-meta-label">Source:</span>
          <span className="swot-tooltip-meta-value">{data.source}</span>
        </div>
      )}
    </>
  );
};

const SwotRingChart: React.FC<SwotRingChartProps> = ({
  areas,
  size = 500,
  onItemClick,
}) => {
  const [selectedItem, setSelectedItem] = useState<SubDimension | null>(null);
  const [hoveredCategory, setHoveredCategory] = useState<string | null>(null);
  const [tooltipData, setTooltipData] = useState<any>(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  // Track mouse position
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePos({ x: e.clientX, y: e.clientY });
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Transform data to Nivo format grouped by SWOT category
  const chartData = useMemo((): NivoNode => {
    const swotGroups: Record<string, { dim: string; sub: SubDimension; dimIndex: number }[]> = {
      S: [], O: [], W: [], T: [],
    };

    areas.forEach(area => {
      area.dimensions.forEach((dim, dimIndex) => {
        dim.sub_dimensions.forEach(sub => {
          if (sub.score !== null && sub.score !== undefined && sub.category) {
            swotGroups[sub.category]?.push({ dim: dim.label, sub, dimIndex });
          }
        });
      });
    });

    const children: NivoNode[] = [];

    ['S', 'O', 'W', 'T'].forEach((cat) => {
      const items = swotGroups[cat];
      const theme = SWOT_THEME[cat as keyof typeof SWOT_THEME];
      
      if (items.length === 0) {
        children.push({
          id: cat,
          name: theme.label,
          color: theme.primary,
          value: 25,
          category: cat,
          depth: 1,
        });
        return;
      }

      // Group by dimension
      const byDimension: Record<string, { sub: SubDimension; dimIndex: number }[]> = {};
      items.forEach(item => {
        if (!byDimension[item.dim]) {
          byDimension[item.dim] = [];
        }
        byDimension[item.dim].push({ sub: item.sub, dimIndex: item.dimIndex });
      });

      const dimensionChildren: NivoNode[] = Object.entries(byDimension).map(([dimName, dimItems], dimIdx) => {
        const subChildren: NivoNode[] = dimItems.map((item) => ({
          id: `${cat}-${dimName}-${item.sub.key}`,
          name: item.sub.label,
          value: Math.max(item.sub.score || 1, 10),
          color: getLayerColor(cat, 3, dimIdx),
          category: cat,
          score: item.sub.score,
          source: item.sub.source,
          depth: 3,
          dimensionName: dimName,
          itemData: item.sub,
        }));

        return {
          id: `${cat}-${dimName}`,
          name: dimName,
          color: getLayerColor(cat, 2, dimIdx),
          children: subChildren,
          category: cat,
          depth: 2,
        };
      });

      children.push({
        id: cat,
        name: theme.label,
        color: theme.primary,
        children: dimensionChildren,
        category: cat,
        depth: 1,
      });
    });

    return {
      id: 'root',
      name: 'SWOT',
      children,
    };
  }, [areas]);

  // Calculate stats
  const stats = useMemo(() => {
    const counts = { S: 0, O: 0, W: 0, T: 0 };
    let totalScore = 0;
    let count = 0;

    areas.forEach(area => {
      area.dimensions.forEach(dim => {
        dim.sub_dimensions.forEach(sub => {
          if (sub.score !== null && sub.category) {
            counts[sub.category as keyof typeof counts]++;
            totalScore += sub.score;
            count++;
          }
        });
      });
    });

    return {
      counts,
      average: count > 0 ? Math.round(totalScore / count) : 0,
      total: count,
    };
  }, [areas]);

  const handleClick = (node: any) => {
    if (node.data.itemData) {
      setSelectedItem(node.data.itemData);
      onItemClick?.(node.data.itemData);
    }
  };

  // Custom tooltip that captures data for portal rendering
  const CustomTooltip = ({ data }: any) => {
    useEffect(() => {
      setTooltipData(data);
      return () => setTooltipData(null);
    }, [data]);
    
    return null;
  };

  // Legend items
  const legendItems = useMemo(() => {
    const groups: Record<string, { label: string; score: number; source?: string; points?: string[] }[]> = {
      S: [], O: [], W: [], T: [],
    };

    areas.forEach(area => {
      area.dimensions.forEach(dim => {
        dim.sub_dimensions.forEach(sub => {
          if (sub.score !== null && sub.category) {
            groups[sub.category]?.push({ 
              label: sub.label, 
              score: sub.score,
              source: sub.source,
              points: sub.points,
            });
          }
        });
      });
    });

    return groups;
  }, [areas]);

  // Auto-select first item on load (prioritize Strength, then others)
  useEffect(() => {
    if (selectedItem) return; // Don't override if already selected
    
    // Find first available item, prioritizing S > O > W > T
    for (const cat of ['S', 'O', 'W', 'T'] as const) {
      const items = legendItems[cat];
      if (items.length > 0) {
        const firstItem = items[0];
        // Find the full sub_dimension data to get category
        for (const area of areas) {
          for (const dim of area.dimensions) {
            for (const sub of dim.sub_dimensions) {
              if (sub.label === firstItem.label && sub.category === cat) {
                setSelectedItem(sub);
                return;
              }
            }
          }
        }
      }
    }
  }, [legendItems, areas, selectedItem]);

  return (
    <div className="swot-chart-container">
      {/* Top: Chart + Vertical Stats Bar */}
      <div className="swot-chart-row">
        {/* Left: Chart */}
        <div className="swot-chart-wrapper">
          <div className="swot-chart" style={{ width: size, height: size }}>
            <ResponsiveSunburst
              data={chartData}
              margin={{ top: 10, right: 10, bottom: 10, left: 10 }}
              id="id"
              value="value"
              cornerRadius={3}
              borderWidth={2}
              borderColor="#1a1a1a"
              colors={(node: any) => node.data.color || '#4b5563'}
              inheritColorFromParent={false}
              childColor={{ from: 'color', modifiers: [['brighter', 0.2]] }}
              enableArcLabels={true}
              arcLabelsSkipAngle={12}
              arcLabelsTextColor="#ffffff"
              arcLabel={(node: any) => {
                if (node.depth === 1) {
                  return node.id;
                }
                return '';
              }}
              onClick={handleClick}
              tooltip={CustomTooltip}
              animate={true}
              motionConfig="gentle"
              transitionMode="pushIn"
            />
          </div>
          
          {/* Center Stats Overlay */}
          <div className="swot-center-overlay">
            <div className="swot-center-value">{stats.average}</div>
            <div className="swot-center-label">Avg Score</div>
            <div className="swot-center-total">{stats.total} metrics</div>
          </div>
        </div>

        {/* Right: Vertical Stats Bar with Selected Detail */}
        <div className="swot-stats-vertical">
          {(['S', 'O', 'W', 'T'] as const).map(cat => {
            const theme = SWOT_THEME[cat];
            const isSelectedCategory = selectedItem?.category === cat;
            
            return (
              <div 
                key={cat} 
                className={`swot-stat-vertical-item ${hoveredCategory === cat ? 'active' : ''} ${isSelectedCategory ? 'selected' : ''}`}
                onMouseEnter={() => setHoveredCategory(cat)}
                onMouseLeave={() => setHoveredCategory(null)}
                onClick={() => {
                  const el = document.getElementById(`swot-breakdown-${cat}`);
                  if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }}
              >
                <div className="swot-stat-vertical-header">
                  <div className="swot-stat-vertical-icon" style={{ backgroundColor: theme.primary }}>
                    {cat}
                  </div>
                  <div className="swot-stat-vertical-info">
                    <span className="swot-stat-vertical-label">{theme.label}</span>
                    <span className="swot-stat-vertical-count" style={{ color: theme.primary }}>{stats.counts[cat]}</span>
                  </div>
                </div>
                
                {/* Selected Item Detail - shown in this category */}
                {isSelectedCategory && selectedItem && (
                  <div className="swot-stat-selected-detail">
                    <div className="swot-stat-selected-header">
                      <span className="swot-stat-selected-name">{selectedItem.label}</span>
                      <button onClick={() => setSelectedItem(null)} className="swot-stat-selected-close">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M18 6L6 18M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    <div className="swot-stat-selected-score" style={{ color: theme.primary }}>
                      {selectedItem.score}
                    </div>
                    {selectedItem.source && (
                      <div className="swot-stat-selected-source">
                        {selectedItem.source}
                      </div>
                    )}
                    {selectedItem.points && selectedItem.points.length > 0 && (
                      <div className="swot-stat-selected-points">
                        {selectedItem.points.map((point, pi) => (
                          <div key={pi} className="swot-stat-selected-point">
                            {point}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Bottom: Full SWOT Breakdown List */}
      <div className="swot-breakdown-list">
        {(['S', 'O', 'W', 'T'] as const).map(cat => {
          const items = legendItems[cat];
          const theme = SWOT_THEME[cat];
          if (items.length === 0) return null;
          
          return (
            <div 
              key={cat}
              id={`swot-breakdown-${cat}`}
              className={`swot-breakdown-section ${hoveredCategory === cat ? 'highlighted' : ''}`}
              onMouseEnter={() => setHoveredCategory(cat)}
              onMouseLeave={() => setHoveredCategory(null)}
            >
              <div className="swot-breakdown-header">
                <div className="swot-breakdown-icon" style={{ backgroundColor: theme.primary }}>
                  {cat}
                </div>
                <div className="swot-breakdown-title">
                  <span className="swot-breakdown-name">{theme.label}</span>
                  <span className="swot-breakdown-count">{items.length} items</span>
                </div>
              </div>
              
              <div className="swot-breakdown-items">
                {items.map((item, i) => (
                  <div key={i} className="swot-breakdown-item">
                    <div className="swot-breakdown-item-main">
                      <div className="swot-breakdown-item-info">
                        <span className="swot-breakdown-item-name">{item.label}</span>
                        {item.source && <><span className="swot-breakdown-item-separator">|</span><span className="swot-breakdown-item-source">{item.source}</span></>}
                      </div>
                      <div className="swot-breakdown-item-score">
                        <div 
                          className="swot-breakdown-item-bar"
                          style={{ 
                            width: `${item.score}%`,
                            backgroundColor: theme.primary,
                          }}
                        />
                        <span className="swot-breakdown-item-value">{item.score}</span>
                      </div>
                    </div>
                    
                    {/* Points */}
                    {item.points && item.points.length > 0 && (
                      <div className="swot-breakdown-item-points">
                        {item.points.map((point, pi) => (
                          <div key={pi} className="swot-breakdown-point">
                            <span className="swot-breakdown-point-icon">→</span>
                            <span className="swot-breakdown-point-text">{point}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Portal tooltip - renders at body level, positioned to the right of cursor */}
      {tooltipData && createPortal(
        <div 
          className="swot-tooltip-portal"
          style={{
            position: 'fixed',
            left: mousePos.x + 15,
            top: mousePos.y - 10,
            zIndex: 999999,
            pointerEvents: 'none',
          }}
        >
          <div className="swot-tooltip">
            <TooltipContent data={tooltipData} />
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default SwotRingChart;
